import React from 'react';
import { Route, Switch } from 'react-router-dom/es';
import { Box } from 'signoxe/components/layout';
import Icon from '../widgets/Icon';
import './styles.scss';

const HelpMessageRoutes = () => (
  <Box direction="column" className="help-message">
    <Switch>
      <Route exact path="/assets" render={() => (
        <article>
          <p>On this page you can view the media assets available to you. Clicking on an asset will
             let you view it in full. You can click on
             the <strong>List</strong> and <strong>Tiles</strong> buttons to switch between viewing
             assets in a List or as tiles. You can also click on <strong>Name</strong>,&nbsp;
            <strong>Type</strong>, and <strong>Time</strong> buttons to sort the assets. Clicking on
             the same sort type twice will reverse the sort direction.</p>
          <p><strong>Video Assets</strong> are video clips in the MP4 or WEBM format.</p>
          <p><strong>Image Assets</strong> are image files in PNG or JPEG format.
                                           JPEG is strongly recommended.</p>
          <p><strong>Web Assets</strong> are web pages in the HTML format.</p>
          <p><strong>Feed Assets</strong> are feeds of videos, images, or web content that offer
                                          new content on a daily basis.</p>
          <p><strong>Calendar Assets</strong> fill in details from calendar events
                                              into a web page allowing you can alter content
                                              according
                                              to a calendar schedule.</p>
          <p>You can use the search bar to search for assets by name. Clicking on the filter
             icon <Icon name="filter" margin={0}/> will show the filter bar which you can use to
             filter assets. You can use the filter bar to display only assets of a single kind, or
             of only portrait or landscape orientation or you can click on one or more tags to only
             show assets that have all the selected tags. NOTE: filtering by aspect ratio (portrait
             / landscape) will only show images and videos.</p>
          <p>You can rename an asset by double clicking its name, and you can click on the trash can
             icon <Icon name="trash" margin={0}/> to delete an asset.</p>
          <p>To operate on multiple assets at the same time you can click
             the <strong>Edit Mode</strong> button. In edit mode you can select assets by clicking
             on them.</p>
            <p><strong>You can use the resumable feature to upload files larger than 30MB, and resume from last
             saved point in the event of network or browser issues, e.t.c.
             All you need to do is re-upload the same file and we'll take it from there.</strong>
            </p>
        </article>
      )}/>
      <Route path="/assets/(new|edit)/web" render={() => (
        <article>
          <p>Here you can add or edit a web asset by either using one of the available templates, or
             by entering raw HTML code. If you are entering raw HTML code know that any images or
             other external assets you reference will be loaded off the internet on the device. </p>
          <p>Enable the <strong>Use template</strong> option to use the simpler template based web
             editor. Each template will allow you to build a web page by filling in values for parts
             of the page. If you have already entered some raw HTML, this will overwrite that.</p>
        </article>
      )}/>
      <Route path="/assets/(new|edit)/cal" render={() => (
        <article>
          <p>Here you can add or edit a calendar asset. A calendar asset needs a name, a URL for the
             calendar and a template. The name and description of the event will be filled into the
             title, and content areas of the template as you can see in the preview. </p>
          <p>You can use any calendar provider as long as it publishes a ICS feed that is publicly
             accessible. Google Calendar is one option.</p>
          <p>We periodically check for new events you add to the calendar, however the changes you
             make are not instantly reflected. If you have added an event and would like the changes
             to be reflected on the same day you can click on the <strong>Refresh</strong> button
             next to the URL field. This button is only available when you are editing an existing
             calendar asset.</p>
        </article>
      )}/>
      <Route path="/assets/:id" render={() => (
        <article>
          <p>On this page you can preview assets. You can also double-click on the asset name on top
             of the screen to change it. The expand icon <Icon name="expand" margin={0}/> on the
             top-right of the page can be clicked to view the asset in full-screen.</p>
          <p>For web and calendar assets you will also see an edit
             icon <Icon name="edit" margin={0}/> that you can click to modify the asset.</p>
          <p>For assets you create or upload you can add tags to them to make it easier to filter
             assets. You can add tags using the textbox below the asset preview. Entering a comma
             or pressing enter will apply the tag. Tags can have spaces and other special characters
             other than a comma.</p>
          <p>For images and videos you will also be able to see some metadata about the image
             or video. Clicking on the <strong>More Details</strong> button will show you the raw
             and detailed metadata.</p>
        </article>
      )}/>

      <Route exact path="/devices/unassigned" render={() => (
        <article>
          <p>This page displays devices that are not part of any group. You can click on the&nbsp;
            <Icon name="group" margin={0}/> icon under <strong>Change Group</strong> to assign a
             device to a group.</p>
        </article>
      )}/>

      <Route exact path="/devices/:id" render={() => (
        <article>
          <p>On this page you can see screenshots captured from the device. You can click on the
             screenshot image to view and download the larger image.</p>
          <p>You can click on the <strong>Take Screenshot</strong> button to send a screenshot
             request
             to the device. Screenshots are not instantaneous, it can take some time for a
             screenshot
             to appear here after a request.</p>
          <p>The device can only process a single screenshot request at a time. Clicking on the
            <strong>Take Screenshot</strong> button multiple times will not take multiple
             screenshots.
             you can only get a new screenshot after the first one has been processed and appears
             here.</p>
          <p>To take multiple screenshots in succession, you can click the&nbsp;
            <strong>Take Screenshot Burst</strong> button. It will take a series of 5 screenshots
             with a gap of 10 seconds.</p>
        </article>
      )}/>

      <Route exact path="/device-groups" render={() => (
        <article>
          <p>On this page you can manage device groups and devices. All devices in a device group
             will get the same content. You can click on <Icon name="calendar-o" margin={0}/> (the
             calendar icon) to view and edit the content schedule for a device group. If this icon
             is
             green you can hover over it to get a preview of the schedule or click it to change the
             content schedule.</p>
          <p>For groups without a schedule you can select a playlist in the first dropdown, and a
             ticker series from the second dropdown. The preview button next to them will open show
             preview of the playlist and ticker combination in the browser. </p>
          <p>The two numbers after that represent the default
             duration (in milliseconds) for web content and images respectively. If you enable&nbsp;
            <strong>Overlay Ticker</strong> the ticker will appear over the video, hiding the part
             behind the ticker. Enabling <strong>Show Time</strong> will display the time on the
             bottom-right corner of the screen.</p>
          <p><strong>Last Ping</strong> shows how long ago the device connected updated its content.
          </p>
          <p>The <Icon name="group" margin={0}/> icon in <strong>Change Group</strong> column allows
             you to reassign a device to a different group.</p>
          <p>You can click on the <strong>View Unassigned Devices</strong> link on the top-right of
             the page to view devices that have not been assigned any group yet.</p>
          <p>You can double-click on any group or device name to go edit it.</p>
        </article>
      )}/>
      <Route path="/device-groups/:id/schedule" render={() => (
        <article>
          <p>Devices that have scheduling enabled can specify different content for different days
             of the week, and for different times of day.</p>
          <p>Under the <strong>Content for Special Occasions</strong> header you can set up special
             content to be played on particular days of the year. For instance you can use it to
             wish
             people a Happy New Year, or even highlight special occasions for your organisation. If
             set up content here, it will override any regular weekly scheduled content you have set
             up.</p>
          <p>You must explicitly enable scheduling device to be able to set a weekly schedule.</p>
          <p>Once enabled you will see headers for each week day. You can click on the heading to
             edit the content for that day, and for different times on that day.</p>
          <p>The content in the <strong>Default content</strong> section will play by default when
             the current time is not in any timeslot.</p>
        </article>
      )}/>

      <Route exact path="/playlists" render={() => (
        <article>
          <p>Playlists allow you to queue up media content for playback on device.</p>
          <p>This page lists all the playlists you have created and lets you preview, add, delete
             and clone playlists.</p>
          <ul>
            <li>The preview icon <Icon name="eye" margin={0}/> will preview the playlist in the
                browser.
            </li>
            <li>The clone icon <Icon name="clone" margin={0}/> will duplicate the playlist.</li>
            <li>The delete icon <Icon name="trash-o" margin={0}/> will delete the playlist.</li>
          </ul>
          <p>The <strong>Add Playlist</strong> and <strong>Add Playlist with Feeds</strong> buttons
             both let you create a new playlist, however the option with feeds will automatically
             subscribe the playlist to new content published by us.</p>
        </article>
      )}/>
      <Route path="/playlists/:id" render={() => (
        <article>
          <p>On this page you can preview, add, remove and reorder media items in a playlist. You
             can also set playlist items to expire at a particular time. For some playlist items
             you can also set their duration. For items that don&#39;t have an empty duration the
             default duration specified for the device group will be used.</p>
          <p>To set the duration you can enter a value in milliseconds (so enter 20000 for 20
             seconds) in the text area next to the clock icon <Icon name="clock-o" margin={0}/>.</p>
          <p>To have an item expire at a particular point of time in the future, click on the
             calendar icon to pick a date, and the clock icon next to it to select a time. After
             this point of time the item will remain in the playlist, but will not play on the
             device.</p>
          <p>You can always change the expiry time, and you can remove the expiry time entirely by
             selecting the date time text and pressing delete / backspace. Expired items will be
             highlighted in red.</p>
          <p>You can reorder items by clicking the drag handler <Icon name="bars" margin={0}/> on
             the left side and moving them to the position you want. If you want to reorder items in
             bulk, you can click the <strong>Reorder</strong> button.</p>
          <p>If you have <strong>Subscribe to new content...</strong> selected, then we will
             automatically add new feeds of content as we make them available.</p>
          <p>If you click on <strong>Add Item</strong> you will see a list of assets on the right
             side of the page. You can use that interface to search for assets, and then drag and
             drop them on the drop area on the top of the playlist. New items will appear on the
             top but you can put them in the place you want after they have been added.</p>
          <p>You can also click on the <strong>Preview in Browser</strong> button to preview the
             playlist in the browser.</p>
        </article>
      )}/>

      <Route exact path="/tickers" render={() => (
        <article>
          <p>A <strong>Ticker Series</strong> is like a playlist of text messages that will scroll
             at the bottom of the screen. On this page you can add new ticker series, and copy or
             delete existing ones.</p>
          <p>You can click on the <Icon name="clone" margin={0}/> (clone icon) to make a copy of an
             existing ticker series, or click on the trash icon <Icon name="trash-o" margin={0}/> to
             delete it. You can click on the name to edit the series itself.</p>
        </article>
      )}/>
      <Route path="/tickers/:id" render={() => (
        <article>
          <p>On this page you can add, edit, remove and reorder individual ticker messages in a
             ticker
             series.</p>
          <p>For each entry you add you will be able to enter text in the box on top, and specify
             the speed, font family, font size, text colour, outline colour, and background
             colour. Clicking on the icon next to the colour will reset it to the default
             colour.</p>
          <p>You can edit an existing ticker message by double-clicking on it. You can click on the
             clone icon <Icon name="clone" margin={0}/> to make a copy of a ticker. This is useful
             if you want to reuse the same font, speed, colour etc. options. Make a copy of an
             existing ticker and just edit the message.</p>
          <p>If you want to reorder the tickers, you can use the drag icon&nbsp;
            <Icon name="bars" margin={0}/> on the left.</p>

        </article>
      )}/>
      <Route exact path="/profile" render={() => (
        <article>
          <p>This page displays and lets you edit the profile of your organisation. The details here
             represent the organisation and not your personal account, and as such they are shared
             with all account holders in the same organisation.</p>
        </article>
      )}/>
      <Route exact path="/profile/change-password" render={() => (
        <article>
          <p>You can use this page to change your account password if you need to.</p>
        </article>
      )}/>
      <Route exact path="/profile/change-email" render={() => (
        <article>
          <p>You can use this page to change your account email if you need to.</p>
        </article>
      )}/>
      <Route path="/notifications" render={() => (
        <article>
          <p>On this page you can read the updates about new features, and other relevant
             information about our service. </p>
          <p>This page will list notifications from the past month. Older updates may also be
             visible if they are unread.</p>
          <p>Clicking on a notification title will open / close it. Opening an unread notification
             automatically marks it as read. </p>
          <p>You can also click on the <strong>Mark all read</strong> button to mark all
             notifications as read.</p>
        </article>
      )}/>
    </Switch>
  </Box>
);

export default HelpMessageRoutes;



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./src/components/HelpMessageRoutes/index.js